import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * Shared shell for legal / policy pages.
 * Public — no auth required. Mobile-first, luxury black/gold aesthetic.
 */
export default function LegalPageShell({ title, eyebrow, lastUpdated, children, testId }) {
  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F8F8] font-body" data-testid={testId}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass-strong">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
            <span className="font-display text-xl tracking-tight text-gold">Wanted</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
          </Link>
          <Link to="/" className="text-xs tracking-overline text-white/50 hover:text-white transition-colors" data-testid="legal-back">
            ← Home
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-gold" />
          <span className="text-gold-soft tracking-overline text-xs">{eyebrow}</span>
        </div>
        <h1 className="font-display font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight" data-testid="legal-title">
          {title}
        </h1>
        {lastUpdated && (
          <p className="text-xs text-white/40 tracking-wide mt-6">LAST UPDATED · {lastUpdated.toUpperCase()}</p>
        )}

        <div className="prose-legal mt-12 space-y-10 text-white/75 leading-relaxed font-light">
          {children}
        </div>

        <div className="mt-20 pt-10 border-t border-white/10 text-sm text-white/40">
          <p>
            Questions? Write to us at{" "}
            <a href="mailto:hello@joinwanted.com" className="text-gold hover:text-gold-soft transition-colors">
              hello@joinwanted.com
            </a>
            . We read everything.
          </p>
        </div>
      </motion.main>

      {/* Compact public footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
          <div className="font-display text-base tracking-tight text-gold">Wanted</div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/40">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/refunds" className="hover:text-white transition-colors">Refunds</Link>
            <Link to="/community-guidelines" className="hover:text-white transition-colors">Community</Link>
          </nav>
          <div className="text-white/30 tracking-overline">© {new Date().getFullYear()} WANTED</div>
        </div>
      </footer>
    </div>
  );
}

/* Heading & paragraph helpers for legal docs */
export function H2({ children, id }) {
  return (
    <h2 id={id} className="font-display text-2xl text-white mt-12 mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

export function P({ children }) {
  return <p className="text-[15px] leading-[1.75]">{children}</p>;
}

export function UL({ children }) {
  return <ul className="list-disc list-outside pl-5 space-y-2 text-[15px] leading-[1.75]">{children}</ul>;
}
